import type * as THREE from 'three/webgpu';
import type {CollectSource} from '../../mesh/sketch/ISketchObject';
import {
    isSketchArcObject,
    isSketchCollectionObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../mesh/guards';

//------ 草图闭合模块化 开始------
/**
 * 草图闭合候选过滤器。
 *
 * 设计说明：
 * 1. 绘制、Trim、属性编辑后的闭合收口都应使用同一套候选过滤规则；
 * 2. 该类只判断“能不能参与闭合搜索”，不负责创建集合或修改对象归属；
 * 3. 保持运行时 flag 判断，避免闭合模块反向依赖 `SketchCollectionObject` 的具体实现。
 */
export class SketchClosureCandidateFilter {
    /**
     * 从草图对象列表中收集可参与闭合解析的集合源。
     *
     * @param objects 草图组内边对象列表。
     * @returns 可参与闭合解析的集合源列表。
     */
    public collectAvailableSources(objects: readonly THREE.Object3D[]): CollectSource[] {
        return objects
            .filter((object): object is CollectSource => this.isCollectSourceForSketchClosure(object))
            .filter((source) => this.isCollectSourceAvailableForSketchClosure(source));
    }

    /**
     * 判断对象是否是草图闭合解析支持的集合源。
     *
     * @param object 待判断对象。
     * @returns 支持参与闭合解析时返回 true。
     */
    public isCollectSourceForSketchClosure(object: THREE.Object3D): object is CollectSource {
        return isSketchLineObject(object)
            || isSketchArcObject(object)
            || (isSketchEllipseObject(object) && object.isPartialEllipse())
            || isSketchParabolaObject(object)
            || isSketchSplineObject(object)
            || isSketchStyleSplineObject(object);
    }

    /**
     * 判断集合源是否可参与草图闭合解析。
     *
     * @param source 待判断集合源。
     * @returns 可参与闭合解析时返回 true。
     */
    public isCollectSourceAvailableForSketchClosure(source: CollectSource): boolean {
        // 完整椭圆虽然与部分椭圆共用运行时类型，但自身已经闭合，绝不能把轴控制点当作开放弧端点。
        if (!this.isCollectSourceForSketchClosure(source)) {
            return false;
        }
        if (this.isCollectSourceLockedByClosedCollection(source)) {
            return false;
        }
        if (source.isConstruction) {
            return false;
        }
        if (isSketchLineObject(source) && source.isInfiniteLength) {
            return false;
        }
        return true;
    }

    /**
     * 判断集合源是否已经被闭合集合或非集合闭合宿主锁定。
     *
     * 说明：
     * 1. 闭合集合中的边已经形成有效 profile，不应被另一个闭合收口重复占用；
     * 2. 开链集合中的边仍可参与二次收口，收口成功后由 `SketchGroup` 释放旧开链绑定；
     * 3. 槽口等非集合闭合宿主视为强锁定，避免破坏专用对象的拓扑语义。
     *
     * @param source 待判断集合源。
     * @returns 被锁定时返回 true。
     */
    private isCollectSourceLockedByClosedCollection(source: CollectSource): boolean {
        const closedCollection = Reflect.get(source, 'closedCollection');
        if (!closedCollection) {
            return false;
        }
        if (isSketchCollectionObject(closedCollection)) {
            return closedCollection.isClosedGeometry;
        }
        return true;
    }
}
//------ 草图闭合模块化 结束------
